<template>
  <component
    :is="baseComponent"
    :to="link.path ? link.path : '/'"
    v-bind="routerLinkClassProps(baseComponent)"
    class="nav-item"
    :class="{ active: isActive }"
    tag="li"
  >
    <a
      v-if="isMenu"
      class="sidebar-menu-item nav-link"
      :class="{ active: isActive }"
      :aria-expanded="!collapsed"
      data-toggle="collapse"
      @click.prevent="collapseMenu"
    >
      <template v-if="addLink">
        <span class="nav-link-text">
          {{ link.name }} <b class="caret"></b>
        </span>
      </template>
      <template v-else>
        <i :class="link.icon"></i>
        <span class="nav-link-text">{{ link.name }} <b class="caret"></b></span>
      </template>
    </a>

    <collapse-transition>
      <div
        v-if="$slots.default || this.isMenu"
        v-show="!collapsed"
        class="collapse show"
      >
        <ul class="nav nav-sm flex-column">
          <slot></slot>
        </ul>
      </div>
    </collapse-transition>

    <slot
      name="title"
      v-if="children.length === 0 && !$slots.default && link.path"
    >
      <component
        :to="link.path"
        @click.native="linkClick"
        :is="elementType(link, false)"
        class="nav-link"
        :class="{ active: link.active || isActive }"
        v-bind="leafLinkProps(elementType(link, false))"
        :target="link.target"
        :href="link.path"
      >
        <template v-if="addLink">
          <span class="nav-link-text">{{ link.name }}</span>
        </template>
        <template v-else>
          <i :class="link.icon"></i>
          <span class="nav-link-text">{{ link.name }}</span>
        </template>
      </component>
    </slot>
  </component>
</template>
<script>
import { CollapseTransition } from 'vue2-transitions';
import { matchesRoute, isLeafActive } from './activeMatch';
import { isSidebarResetPath, isPlainNavClick, navigateWithSidebarReset } from '@/utils/sidebarReset';

// router-link would otherwise add `.active` itself (router.js linkActiveClass)
// to EVERY matching link, lighting /wms/inventory and /wms/inventory/summary
// together. The class is redirected to an unstyled name and `.active` is set
// from isActive (most specific match only, activeMatch.js).
const ROUTER_ACTIVE_CLASS = 'sidebar-router-link-match';

export default {
  name: 'sidebar-item',
  components: {
    CollapseTransition
  },
  props: {
    menu: {
      type: Boolean,
      default: false,
      description:
        "Whether the item is a menu. Most of the item it's not used and should be used only if you want to override the default behavior."
    },
    link: {
      type: Object,
      default: () => {
        return {
          name: '',
          path: '',
          children: []
        };
      },
      description:
        'Sidebar link. Can contain name, path, icon and other attributes. See examples for more info'
    }
  },
  provide() {
    return {
      addLink: this.addChild,
      removeLink: this.removeChild
    };
  },
  inject: {
    addLink: { default: null },
    removeLink: { default: null },
    // SideBar.vue: every rendered item registers here so a leaf can tell
    // whether a more specific leaf also matches the route (activeMatch.js).
    sidebarActiveRegistry: { default: null },
    autoClose: {
      default: true
    }
  },
  data() {
    return {
      children: [],
      collapsed: true
    };
  },
  computed: {
    baseComponent() {
      return this.isMenu || this.link.isRoute ? 'li' : 'router-link';
    },
    linkPrefix() {
      if (this.link.name) {
        let words = this.link.name.split(' ');
        return words.map(word => word.substring(0, 1)).join('');
      }
    },
    isMenu() {
      return this.children.length > 0 || this.menu === true;
    },
    isLinkable() {
      return !!this.link.path && this.link.isRoute !== false;
    },
    // A group: one of its descendants is the page on screen (the header then
    // reads in the accent colour and stays expanded). A leaf: its path is the
    // most specific match for the current route among all sidebar leaves.
    isActive() {
      const route = this.$route;
      if (!route || !route.path) return false;
      if (this.isMenu) {
        return this.children.some(c => c.isActive);
      }
      if (!this.isLinkable) return false;
      if (!matchesRoute(route, this.link.path)) return false;
      const registry = this.sidebarActiveRegistry;
      const all = registry
        ? registry.items
            .filter(i => !i.isMenu && i.isLinkable)
            .map(i => i.link.path)
        : [];
      return isLeafActive(route, this.link.path, all);
    }
  },
  watch: {
    // seller-fn/BO only expanded the active group on mount; navigating to a
    // page in a closed group (taskbar, pinned row, URL) left it shut.
    isActive(active) {
      if (active && this.isMenu) this.collapsed = false;
    }
  },
  methods: {
    // Only a router-link takes these; on a plain <li>/<a> they would render
    // as stray HTML attributes.
    routerLinkClassProps(component) {
      return component === 'router-link'
        ? { activeClass: ROUTER_ACTIVE_CLASS, exactActiveClass: ROUTER_ACTIVE_CLASS }
        : {};
    },
    // The leaf link. For a page that starts over on a sidebar click
    // (src/utils/sidebarReset.js) router-link must not navigate (event: []);
    // it still renders the href and blocks the browser's own navigation on a
    // plain click, and linkClick does the push so it can see an abort.
    leafLinkProps(component) {
      const props = this.routerLinkClassProps(component);
      if (component === 'router-link' && isSidebarResetPath(this.link.path)) {
        return { ...props, event: [] };
      }
      return props;
    },
    addChild(item) {
      const index = this.$slots.default.indexOf(item.$vnode);
      this.children.splice(index, 0, item);
    },
    removeChild(item) {
      const tabs = this.children;
      const index = tabs.indexOf(item);
      tabs.splice(index, 1);
    },
    elementType(link, isParent = true) {
      if (link.isRoute === false) {
        return isParent ? 'li' : 'a';
      } else {
        return 'router-link';
      }
    },
    linkAbbreviation(name) {
      const matches = name.match(/\b(\w)/g);
      return matches.join('');
    },
    linkClick(e) {
      // A sidebar click opens the page fresh (owner 2026-10-16: รายการสั่งซื้อ
      // must not reopen on a packing round). Only paths in SIDEBAR_RESET_PATHS;
      // a taskbar-tab click never comes through here. Modified clicks (new tab)
      // are left to the browser, as router-link leaves them.
      if (isSidebarResetPath(this.link.path) && isPlainNavClick(e)) {
        navigateWithSidebarReset(this.$router, this.link.path);
      }
      if (
        this.autoClose &&
        this.$sidebar &&
        this.$sidebar.showSidebar === true
      ) {
        this.$sidebar.displaySidebar(false);
      }
    },
    collapseMenu() {
      this.collapsed = !this.collapsed;
    },
    collapseSubMenu(link) {
      link.collapsed = !link.collapsed;
    }
  },
  mounted() {
    if (this.sidebarActiveRegistry) {
      this.sidebarActiveRegistry.items.push(this);
    }
    if (this.addLink) {
      this.addLink(this);
    }
    if (this.link.collapsed !== undefined) {
      this.collapsed = this.link.collapsed;
    }
    if (this.isActive && this.isMenu) {
      this.collapsed = false;
    }
  },
  destroyed() {
    if (this.sidebarActiveRegistry) {
      const items = this.sidebarActiveRegistry.items;
      const at = items.indexOf(this);
      if (at !== -1) items.splice(at, 1);
    }
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
    if (this.removeLink) {
      this.removeLink(this);
    }
  }
};
</script>
<style>
.sidebar-menu-item {
  cursor: pointer;
}
</style>
